<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import { STATUS_CODES } from '@/constants'
import { TLDCatalogItem, TLDCatalogResponse } from '@/types/dashboard/domain'
import { FeaturedTLDConfig, FeaturedTLDRequest } from '@/types/admin'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import FeaturedTLDTable from './FeaturedTLDTable.vue'

const notifyStore = useNotifyStore()

const state = reactive({
  loading: false,
  loadingCatalog: false,
  saving: false,
  catalog: [] as TLDCatalogItem[],
  featuredTlds: [] as FeaturedTLDConfig[],
  searchQuery: '',
  categoryFilter: '' as '' | 'generic' | 'cctld' | 'new_gtld' | 'other'
})

const categoryOptions = [
  { title: 'Alle Kategorien', value: '' },
  { title: 'Generic', value: 'generic' },
  { title: 'Country Code', value: 'cctld' },
  { title: 'New gTLD', value: 'new_gtld' },
  { title: 'Other', value: 'other' }
]

// Sortable setup
const sortableEl = ref<HTMLElement | null>(null)
let sortableInstance: Sortable | null = null

const initSortable = () => {
  if (sortableEl.value && !sortableInstance) {
    sortableInstance = Sortable.create(sortableEl.value, {
      handle: '.drag-handle',
      animation: 150,
      onEnd: (evt) => {
        if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
          const item = state.featuredTlds[evt.oldIndex]
          if (item) {
            const rest = state.featuredTlds.filter((_, i) => i !== evt.oldIndex)
            state.featuredTlds = [...rest.slice(0, evt.newIndex), item, ...rest.slice(evt.newIndex)]
          }
          handleReorder()
        }
      }
    })
  }
}

watch(sortableEl, (el) => {
  if (el) initSortable()
})

// Load current featured TLDs (admin endpoint)
const loadFeatured = async () => {
  try {
    const response = await useAxios<never, FeaturedTLDConfig[]>('GET', '/admin/tlds/featured', true)
    if (response.status === STATUS_CODES.OK) {
      const validate = FeaturedTLDConfig.array().safeParse(response.data)
      if (validate.success) {
        state.featuredTlds = [...validate.data].sort((a, b) => a.display_order - b.display_order)
      }
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:loadFeatured' })
  }
}

// Load catalog with server-side search/filtering
const loadCatalog = async (search?: string, category?: string) => {
  state.loadingCatalog = true
  try {
    const allTlds: TLDCatalogItem[] = []
    let offset = 0
    const limit = 200

    while (true) {
      const params = new URLSearchParams()
      params.set('limit', String(limit))
      params.set('offset', String(offset))
      if (search) params.set('search', search)
      if (category) params.set('category', category)

      const response = await useAxios<never, TLDCatalogResponse>(
        'GET',
        `/domain/tlds?${params.toString()}`,
        false
      )
      if (response.status === STATUS_CODES.OK) {
        const validate = TLDCatalogResponse.safeParse(response.data)
        if (validate.success) {
          allTlds.push(...validate.data.tlds)
          if (validate.data.tlds.length < limit) break
          offset += limit
        } else {
          break
        }
      } else {
        break
      }
    }
    state.catalog = allTlds
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:loadCatalog' })
  } finally {
    state.loadingCatalog = false
  }
}

// Debounced reload on search/filter change
let searchTimeout: ReturnType<typeof setTimeout> | null = null
watch([() => state.searchQuery, () => state.categoryFilter], ([search, category]) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadCatalog(search || undefined, category || undefined)
  }, 300)
})

// Add a new featured TLD
const handleAddTld = async (tld: string, category: string) => {
  state.saving = true
  try {
    const displayOrder = state.featuredTlds.length
    const response = await useAxios<FeaturedTLDRequest, FeaturedTLDConfig>(
      'POST',
      '/admin/tlds/featured',
      true,
      { tld, category, display_order: displayOrder, enabled: true }
    )
    if (response.status === STATUS_CODES.OK || response.status === STATUS_CODES.CREATED) {
      await loadFeatured()
      notifyStore.createNotify({
        id: uuid(),
        title: 'Hinzugefugt',
        message: `.${tld} wurde zu Featured TLDs hinzugefugt`,
        alertType: 'success'
      })
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:handleAddTld' })
    notifyStore.createNotify({
      id: uuid(),
      title: 'Fehler',
      message: `Konnte .${tld} nicht hinzufugen`,
      alertType: 'error'
    })
  } finally {
    state.saving = false
  }
}

// Remove a featured TLD
const handleRemoveTld = async (tld: string) => {
  state.saving = true
  try {
    const response = await useAxios<never, void>('DELETE', `/admin/tlds/featured/${tld}`, true)
    if (response.status === STATUS_CODES.NO_CONTENT || response.status === STATUS_CODES.OK) {
      await loadFeatured()
      notifyStore.createNotify({
        id: uuid(),
        title: 'Entfernt',
        message: `.${tld} wurde entfernt`,
        alertType: 'success'
      })
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:handleRemoveTld' })
    notifyStore.createNotify({
      id: uuid(),
      title: 'Fehler',
      message: `Konnte .${tld} nicht entfernen`,
      alertType: 'error'
    })
  } finally {
    state.saving = false
  }
}

// Toggle enabled status
const handleToggleEnabled = async (item: FeaturedTLDConfig) => {
  try {
    await useAxios<FeaturedTLDRequest, FeaturedTLDConfig>(
      'POST',
      '/admin/tlds/featured',
      true,
      { tld: item.tld, category: item.category, enabled: item.enabled, display_order: item.display_order }
    )
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:handleToggleEnabled' })
    // Revert on error
    item.enabled = !item.enabled
  }
}

// Reorder featured TLDs after drag-and-drop
const handleReorder = async () => {
  state.saving = true
  try {
    // Update display_order for all items based on their new positions
    for (let i = 0; i < state.featuredTlds.length; i++) {
      const item = state.featuredTlds[i]
      if (item && item.display_order !== i) {
        await useAxios<FeaturedTLDRequest, FeaturedTLDConfig>(
          'POST',
          '/admin/tlds/featured',
          true,
          { tld: item.tld, category: item.category, display_order: i, enabled: item.enabled }
        )
        // Update local state immediately so UI shows correct order numbers
        item.display_order = i
      }
    }
    await loadFeatured()
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'FeaturedTLDHome:handleReorder' })
  } finally {
    state.saving = false
  }
}

// Filter catalog to show only TLDs not already featured (search/category done server-side)
const filteredCatalog = computed(() => {
  const featuredSet = new Set(state.featuredTlds.map(f => f.tld))
  return state.catalog.filter(item => !featuredSet.has(item.tld))
})

const featuredCount = computed(() => state.featuredTlds.length)
const enabledCount = computed(() => state.featuredTlds.filter(f => f.enabled).length)

// Helper function for category colors
const getCategoryColor = (category: string): string => {
  switch (category) {
    case 'generic': return 'primary'
    case 'cctld': return 'success'
    case 'new_gtld': return 'warning'
    default: return 'default'
  }
}

onMounted(async () => {
  // Load featured first (blocking - needed for initial UI)
  state.loading = true
  await loadFeatured()
  state.loading = false

  // Load catalog in background (non-blocking)
  loadCatalog()
})
</script>

<template>
  <BaseDashboardPage
    title="Featured TLDs"
    subtitle="Konfiguriere die Featured TLDs fur die Domain-Suche"
    icon="mdi-star-circle"
    icon-color="purple"
  >
    <!-- Stats Pills -->
    <template #header-below>
      <div class="stats-pills">
        <div class="stat-pill">
          <v-icon icon="mdi-domain" size="16" />
          <span class="stat-value">{{ featuredCount }}</span>
          <span class="stat-label">Featured</span>
        </div>
        <div class="stat-pill stat-enabled">
          <v-icon icon="mdi-check-circle" size="16" />
          <span class="stat-value">{{ enabledCount }}</span>
          <span class="stat-label">Aktiv</span>
        </div>
        <div class="stat-pill stat-catalog">
          <v-icon icon="mdi-earth" size="16" />
          <span class="stat-value">{{ state.catalog.length }}</span>
          <span class="stat-label">Verfugbar</span>
        </div>
      </div>
    </template>

    <!-- Current Featured TLDs -->
    <section class="featured-section">
      <div class="section-header">
        <h2>Aktive Featured TLDs</h2>
        <span class="hint">Drag & Drop zum Sortieren</span>
      </div>

      <div v-if="state.featuredTlds.length === 0" class="empty-state">
        <v-icon icon="mdi-star-off" size="48" />
        <p>Keine Featured TLDs konfiguriert</p>
      </div>

      <div v-else ref="sortableEl" :class="{ 'sortable-disabled': state.saving }">
        <div
          v-for="element in state.featuredTlds"
          :key="element.tld"
          class="featured-tld-item"
          :class="{ disabled: !element.enabled }"
        >
          <v-icon icon="mdi-drag" class="drag-handle" />
          <span class="tld-order">#{{ element.display_order + 1 }}</span>
          <span class="tld-name">.{{ element.tld }}</span>
          <v-chip size="x-small" :color="getCategoryColor(element.category)">
            {{ element.category }}
          </v-chip>
          <v-switch
            v-model="element.enabled"
            hide-details
            density="compact"
            color="success"
            @change="handleToggleEnabled(element)"
          />
          <v-btn
            icon="mdi-delete"
            variant="text"
            color="error"
            size="small"
            :disabled="state.saving"
            @click="handleRemoveTld(element.tld)"
          />
        </div>
      </div>

      <v-progress-linear v-if="state.saving" indeterminate color="primary" class="mt-2" />
    </section>

    <!-- Add New TLDs -->
    <section class="catalog-section">
      <div class="section-header">
        <h2>TLD hinzufugen</h2>
      </div>

      <div class="filters-row">
        <div class="search-box">
          <v-icon icon="mdi-magnify" size="18" />
          <input
            v-model="state.searchQuery"
            type="text"
            placeholder="TLD suchen..."
            class="search-input"
          />
          <button v-if="state.searchQuery" class="clear-btn" @click="state.searchQuery = ''">
            <v-icon icon="mdi-close" size="16" />
          </button>
        </div>

        <v-select
          v-model="state.categoryFilter"
          :items="categoryOptions"
          density="compact"
          hide-details
          variant="outlined"
          class="category-select"
        />
      </div>

      <FeaturedTLDTable
        :items="filteredCatalog"
        :loading="state.loadingCatalog"
        :disabled="state.saving"
        @add="handleAddTld"
      />
    </section>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Stats Pills */
.stats-pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--dashboard-gap-sm, 8px);
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm, 8px);
  padding: 8px 12px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 24px;

  .stat-value {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .stat-label {
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
  }

  &.stat-enabled {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);

    .v-icon { color: #81c784; }
    .stat-value { color: #81c784; }
  }

  &.stat-catalog {
    background: rgba(33, 150, 243, 0.08);
    border-color: rgba(33, 150, 243, 0.2);

    .v-icon { color: #64b5f6; }
    .stat-value { color: #64b5f6; }
  }
}

/* Sections */
.featured-section,
.catalog-section {
  padding: 24px;
  background: rgba(15, 23, 48, 0.4);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .hint {
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.4);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: rgba(200, 218, 255, 0.4);

  .v-icon {
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
  }
}

.sortable-disabled {
  pointer-events: none;
  opacity: 0.7;
}

/* Featured TLD Item */
.featured-tld-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(120, 163, 255, 0.2);
  }

  &.disabled {
    opacity: 0.5;
  }

  .drag-handle {
    cursor: grab;
    color: rgba(200, 218, 255, 0.4);

    &:active {
      cursor: grabbing;
    }
  }

  .tld-order {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.4);
    min-width: 24px;
  }

  .tld-name {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    min-width: 60px;
  }

  .v-switch {
    margin-left: auto;
  }
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  flex: 1;
  min-width: 200px;

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus-within {
    border-color: rgba(156, 39, 176, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.clear-btn {
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: rgba(200, 218, 255, 0.4);

  &:hover {
    color: rgba(200, 218, 255, 0.8);
  }
}

.category-select {
  min-width: 180px;
}

/* Light Theme */
.v-theme--lightTheme {
  .featured-section,
  .catalog-section {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .section-header h2 {
    color: rgba(15, 23, 42, 0.9);
  }

  .featured-tld-item {
    background: rgba(15, 23, 42, 0.03);
    border-color: rgba(15, 23, 42, 0.1);

    .tld-name {
      color: rgba(15, 23, 42, 0.9);
    }
  }
}

@media (max-width: 768px) {
  .filters-row {
    flex-direction: column;
  }

  .category-select {
    min-width: 100%;
  }

  .featured-tld-item {
    flex-wrap: wrap;
  }
}
</style>
